import type { ReactNode } from "react";

import { Button } from "@heroui/react";
import { Sheet } from "@heroui-pro/react";

/** The side sheet for long forms: a title, a scrolling body and a pinned footer with Cancel and the primary action. */
export function SideSheet({
  isOpen,
  onClose,
  title,
  description,
  children,
  submitLabel,
  isSubmitting,
  isSubmitDisabled,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  submitLabel: string;
  isSubmitting?: boolean;
  isSubmitDisabled?: boolean;
  onSubmit: () => void;
}) {
  return (
    <Sheet
      isDetached
      isOpen={isOpen}
      placement="right"
      onOpenChange={(open) => !open && onClose()}
    >
      <Sheet.Backdrop className="z-0">
        <Sheet.Content className="h-[calc(100%-1rem)] w-full max-w-xl">
          <Sheet.Dialog>
            <form
              noValidate
              className="flex h-full min-h-0 flex-col"
              onSubmit={(event) => {
                event.preventDefault();
                onSubmit();
              }}
            >
              <Sheet.CloseTrigger />
              <Sheet.Header>
                <Sheet.Heading>{title}</Sheet.Heading>
                {description ? (
                  <p className="text-muted text-sm">{description}</p>
                ) : null}
              </Sheet.Header>
              <Sheet.Body className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
                {children}
              </Sheet.Body>
              <Sheet.Footer className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onPress={onClose}>
                  Cancel
                </Button>
                <Button
                  isDisabled={isSubmitDisabled}
                  isPending={isSubmitting}
                  type="submit"
                >
                  {submitLabel}
                </Button>
              </Sheet.Footer>
            </form>
          </Sheet.Dialog>
        </Sheet.Content>
      </Sheet.Backdrop>
    </Sheet>
  );
}

/** A titled group of fields inside a side sheet, divided from the one before it. */
export function SheetSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="border-separator flex flex-col gap-4 border-t pt-5 first:border-t-0 first:pt-0">
      <div className="flex flex-col gap-0.5">
        <h3 className="text-sm font-semibold">{title}</h3>
        {description ? (
          <p className="text-muted text-xs">{description}</p>
        ) : null}
      </div>
      {children}
    </section>
  );
}
